import React, { useRef, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, X, FolderOpen } from "lucide-react";
import MediaStatusBadge from "./MediaStatusBadge";
import MediaPicker from "./MediaPicker";

/**
 * MediaInput — single URL input with a small "Upload" button that uploads a
 * file to the object storage and writes the returned URL back into the field.
 *
 * Phase 2 (2026-02-28): added a "Browse" button that opens the new
 * MediaPicker modal so users can pick existing media without copy-pasting
 * URLs. The legacy paste-a-URL input remains for power users and the
 * external-CDN fallback.
 *
 * Props:
 *  - value: string (current URL)
 *  - onChange(url)
 *  - accept: string (file accept list, defaults to image/*,video/*,application/pdf)
 *  - preview: 'thumb' | 'none' (default 'thumb')
 *  - testid: optional data-testid for the URL input
 *  - projectId: optional — attributes the upload to a project (and via that,
 *    to the project's client for storage-quota counting)
 *  - kind: 'upload' | 'poster' | 'thumbnail' | 'frame' (default 'upload')
 *  - kindHint: 'image'|'video'|'panorama'|'pdf' — pre-selects the picker's filter
 *  - preserveOriginal: opt-in for floor plans that must retain their original pixels
 *
 * Pipeline badge (iter25): whenever the URL points at an `/api/files/...`
 * object, we mount a `<MediaStatusBadge>` that polls the new
 * `/api/admin/media/by-path` endpoint and shows a small pill
 * (Processing… / Optimized ✓ / Failed) + a re-process button.
 */
export default function MediaInput({ value, onChange, accept = "image/*,video/*,application/pdf,audio/*", preview = "thumb", testid, projectId, kind = "upload", kindHint, preserveOriginal = false }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [recentMediaId, setRecentMediaId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (projectId) fd.append("project_id", projectId);
      if (kind) fd.append("kind", kind);
      if (preserveOriginal) fd.append("preserve_original", "true");
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data.url;
      setRecentMediaId(r.data.media_id || null);
      onChange(url);
      toast.success("Uploaded");
    } catch (err) {
      if (err?.response?.status === 413) {
        toast.error(err.response.data?.detail || "Storage quota exceeded.");
      } else {
        toast.error(apiErrorMessage(err));
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const isImage = value && /\.(png|jpe?g|webp|gif|avif)(\?|$)/i.test(value) || (value && value.startsWith("/api/files/") && !/\.(mp4|webm|mov|mp3|wav|ogg|m4a)$/i.test(value));
  const isVideo = value && /\.(mp4|webm|mov)(\?|$)/i.test(value);
  const isAudio = value && /\.(mp3|wav|ogg|m4a|aac|flac)(\?|$)/i.test(value);

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Browse, upload, or paste URL"
          className="flex-1 px-3 py-2 rounded-md border border-zinc-300 bg-white outline-none focus:border-zinc-900 text-sm"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-zinc-300 text-sm hover:bg-zinc-50 transition"
          data-testid={testid ? `${testid}-browse` : "media-input-browse"}
          title="Browse Media Library"
        >
          <FolderOpen className="h-4 w-4" /> Browse
        </button>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-zinc-300 text-sm hover:bg-zinc-50 transition disabled:opacity-60"
          data-testid={testid ? `${testid}-upload` : undefined}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Upload
        </button>
        {value && (
          <button type="button" onClick={() => onChange("")} className="px-2 rounded-md hover:bg-zinc-100"><X className="h-4 w-4 text-zinc-500" /></button>
        )}
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={handleFile} />
      </div>
      {preview === "thumb" && value && (
        <div className="w-full max-w-[280px] rounded-md overflow-hidden border border-zinc-200 bg-zinc-50">
          {isVideo ? (
            <video src={value} className="w-full h-24 object-cover" muted preload="metadata" />
          ) : isAudio ? (
            <audio src={value} controls className="w-full" preload="metadata" />
          ) : isImage ? (
            <img src={value} alt="" className="w-full h-24 object-cover" />
          ) : (
            <div className="px-3 py-2 text-xs text-zinc-600 truncate font-mono">{value}</div>
          )}
        </div>
      )}
      {value && value.includes("/api/files/") && (
        <MediaStatusBadge url={value} mediaId={recentMediaId} />
      )}
      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={(url, media) => { onChange(url); if (media?.id) setRecentMediaId(media.id); }}
        projectId={projectId}
        accept={accept}
        kindHint={kindHint}
        preserveOriginal={preserveOriginal}
      />
    </div>
  );
}
